/**
 * The BI Dashboard on a live account: the panels the book can now answer.
 *
 * The page said origination volume, conversion and time to close could not be
 * computed because funded loans had no funded dates. The 36 funded loans on the
 * book carry them now (2 Nov 2026), and the deal analytics API
 * (`/api/v1/dashboards/analytics`, built on the Pipeline grid's own per-deal
 * facts) answers by month and by stage. Every figure here is that API's, with
 * the basis it states; every count opens the deals it counts (the universal
 * drill). What still has no source or no agreed definition says so: revenue
 * attribution and the originator scorecard (R10/R11), and time to fund, which
 * needs a received date the deal book does not state.
 */
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { BarChart3 } from 'lucide-react'
import api from '@/services/api'
import DrillDownDialog from '@/components/dashboards/DrillDownDialog'
import { useDrillDown } from '@/components/dashboards/useDrillDown'

type Bucket = { count: number; amount: number | null; dealIds: string[] }
type FlowPoint = { month: string; label: string; funded: Bucket }
type FlowResponse = { points: FlowPoint[]; basis: string }
type FunnelStage = {
  stage: string
  label: string
  count: number
  amount: number | null
  dealIds: string[]
  convToNextPct: number | null
}
type FunnelResponse = { stages: FunnelStage[]; dead: { count: number; dealIds: string[] }; basis?: string }
type PivotMeasures = {
  count: number
  fundedCount: number
  avgDaysToFund: number | null
  avgDaysToFundN: number
  dealIds: string[]
}
type PivotResponse = { totals: { measures: PivotMeasures } }
type PivotRow = {
  key: string | null
  label: string
  measures: { count: number; committedCount: number; fundedCount: number; commitment: number | null; dealIds: string[] }
}
type BrokeragePivot = { rows: PivotRow[]; basis?: string }

const ANALYTICS = '/dashboards/analytics'

export const biAnalytics = {
  flow: () => api.post<FlowResponse>(`${ANALYTICS}/flow`, { dateField: 'funded', months: 24 }).then((r) => r.data),
  funnel: () => api.post<FunnelResponse>(`${ANALYTICS}/funnel`, {}).then((r) => r.data),
  totals: () => api.post<PivotResponse>(`${ANALYTICS}/pivot`, { dimension: 'stage' }).then((r) => r.data),
  brokerages: () => api.post<BrokeragePivot>(`${ANALYTICS}/pivot`, { dimension: 'brokerage' }).then((r) => r.data),
}

/** What a book writes where a deal had no broker: not a brokerage to rank. */
const NO_BROKER = new Set(['direct', 'none', 'n/a', 'na', 'no broker', '-', 'unknown'])

/** "Signed" = Term Sheet Signed or later: committed (IC approved, signed, locked) or funded. */
export function topSignedBrokerages(rows: PivotRow[], limit = 10): PivotRow[] {
  return rows
    .filter((row) => row.key && !NO_BROKER.has(row.label.trim().toLowerCase()))
    .filter((row) => row.measures.committedCount + row.measures.fundedCount > 0)
    .sort(
      (a, b) =>
        b.measures.committedCount + b.measures.fundedCount - (a.measures.committedCount + a.measures.fundedCount) ||
        (b.measures.commitment ?? 0) - (a.measures.commitment ?? 0) ||
        a.label.localeCompare(b.label),
    )
    .slice(0, limit)
}

/** Dollars as the desk reads a volume: $12.5M. Null is no figure. */
export function millions(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) return '--'
  return `$${(amount / 1_000_000).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`
}

function pct(value: number | null | undefined): string {
  return value == null || !Number.isFinite(value) ? '--' : `${value.toFixed(1)}%`
}

function DrillCount({ count, ids, title }: { count: number; ids: string[]; title: string }) {
  const { open } = useDrillDown()
  if (count === 0 || ids.length === 0) return <span className="font-mono text-stone-500">{count}</span>
  return (
    <button
      type="button"
      onClick={() => open({ dataset: 'loans', filters: { ids }, title })}
      className="font-mono text-amber-200 underline decoration-amber-400/30 underline-offset-2 hover:text-amber-100"
      title="Open the deals this counts"
    >
      {count}
    </button>
  )
}

function Panel({ title, basis, children }: { title: string; basis?: string | null; children: React.ReactNode }) {
  return (
    <section className="card px-5 py-4">
      <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-stone-500">{title}</div>
      <div className="mt-2">{children}</div>
      {basis ? <p className="mt-2 text-[10px] leading-snug text-stone-600">{basis}</p> : null}
    </section>
  )
}

function PanelState({ loading, error }: { loading: boolean; error: boolean }) {
  if (loading) return <p className="text-[11px] text-stone-500">Reading the book…</p>
  if (error) return <p className="text-[11px] text-red-300">The analytics could not be read. Nothing is shown in their place.</p>
  return null
}

function MonthlyFunded() {
  const flow = useQuery({ queryKey: ['bi', 'flow', 'funded'], queryFn: biAnalytics.flow, staleTime: 60_000, retry: false })
  const points = flow.data?.points ?? []
  const withDeals = points.filter((p) => p.funded.count > 0)
  const peak = Math.max(1, ...points.map((p) => p.funded.amount ?? 0))
  const total = points.reduce((sum, p) => sum + (p.funded.amount ?? 0), 0)
  const count = points.reduce((sum, p) => sum + p.funded.count, 0)
  return (
    <Panel title="Monthly funded volume · last 24 months" basis={flow.data?.basis}>
      <PanelState loading={flow.isLoading} error={flow.isError} />
      {flow.data ? (
        withDeals.length === 0 ? (
          <p className="text-[11px] text-stone-500">No deal funded in the last 24 months.</p>
        ) : (
          <>
            <div className="mb-2 flex items-baseline gap-3 text-[11px] text-stone-400">
              <span className="font-mono text-sm text-stone-100">{millions(total)}</span>
              <span>
                over <DrillCount count={count} ids={points.flatMap((p) => p.funded.dealIds)} title="Funded, last 24 months" /> deals
              </span>
            </div>
            <table className="dense-table w-full" data-testid="bi-monthly-funded">
              <thead>
                <tr>
                  <th>Month</th>
                  <th className="text-right">Deals</th>
                  <th className="text-right">Volume</th>
                  <th className="w-1/2" aria-label="Volume bar" />
                </tr>
              </thead>
              <tbody>
                {withDeals.map((p) => (
                  <tr key={p.month}>
                    <td className="text-stone-300">{p.label}</td>
                    <td className="text-right">
                      <DrillCount count={p.funded.count} ids={p.funded.dealIds} title={`Funded in ${p.label}`} />
                    </td>
                    <td className="text-right font-mono text-stone-200">{millions(p.funded.amount)}</td>
                    <td>
                      <div
                        className="h-2 rounded-sm bg-amber-400/50"
                        style={{ width: `${Math.max(2, ((p.funded.amount ?? 0) / peak) * 100)}%` }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )
      ) : null}
    </Panel>
  )
}

function StageConversion() {
  const funnel = useQuery({ queryKey: ['bi', 'funnel'], queryFn: biAnalytics.funnel, staleTime: 60_000, retry: false })
  return (
    <Panel title="Deals by stage · conversion to the next stage" basis={funnel.data?.basis}>
      <PanelState loading={funnel.isLoading} error={funnel.isError} />
      {funnel.data ? (
        <table className="dense-table w-full" data-testid="bi-stage-conversion">
          <thead>
            <tr>
              <th>Stage</th>
              <th className="text-right">Deals</th>
              <th className="text-right">Requested</th>
              <th className="text-right">To next</th>
            </tr>
          </thead>
          <tbody>
            {funnel.data.stages.map((s) => (
              <tr key={s.stage}>
                <td className="text-stone-300">{s.label}</td>
                <td className="text-right"><DrillCount count={s.count} ids={s.dealIds} title={s.label} /></td>
                <td className="text-right font-mono text-stone-200">{millions(s.amount)}</td>
                <td className="text-right font-mono text-stone-300">{pct(s.convToNextPct)}</td>
              </tr>
            ))}
            <tr>
              <td className="text-stone-500">Dead</td>
              <td className="text-right">
                <DrillCount count={funnel.data.dead.count} ids={funnel.data.dead.dealIds} title="Dead deals" />
              </td>
              <td colSpan={2} className="text-right text-[10px] text-stone-600">
                {funnel.data.dead.count === 0 ? 'No dead deals are loaded yet' : ''}
              </td>
            </tr>
          </tbody>
        </table>
      ) : null}
    </Panel>
  )
}

function TimeToFund() {
  const totals = useQuery({ queryKey: ['bi', 'totals'], queryFn: biAnalytics.totals, staleTime: 60_000, retry: false })
  const m = totals.data?.totals.measures
  const basis = m
    ? `Received to funded, on the funded deals that carry both dates: ${m.avgDaysToFundN} of ${m.fundedCount}. ` +
      (m.avgDaysToFundN === 0
        ? 'The deal book states no received date, so no imported funded deal can be measured yet.'
        : '')
    : null
  return (
    <Panel title="Time to fund" basis={basis}>
      <PanelState loading={totals.isLoading} error={totals.isError} />
      {m ? (
        <div className="font-mono text-2xl text-stone-100" data-testid="bi-time-to-fund">
          {m.avgDaysToFund == null ? '--' : `${Math.round(m.avgDaysToFund)} days`}
        </div>
      ) : null}
    </Panel>
  )
}

/** T-35 (2 Nov 2026, Louis for Randy): "who are our top 10 brokers that we actually signed deals with". */
function TopSignedBrokerages() {
  const pivot = useQuery({ queryKey: ['bi', 'brokerages'], queryFn: biAnalytics.brokerages, staleTime: 60_000, retry: false })
  const rows = pivot.data ? topSignedBrokerages(pivot.data.rows) : []
  return (
    <Panel
      title="Top 10 brokerages by signed deals"
      basis="Signed = Term Sheet Signed or later (IC approved, rate locked or funded), on the deals live now; Direct and blank brokerages are not ranked. Every count opens its deals."
    >
      <PanelState loading={pivot.isLoading} error={pivot.isError} />
      {pivot.data ? (
        rows.length === 0 ? (
          <p className="text-[11px] text-stone-500">No brokerage has a signed deal on the book.</p>
        ) : (
          <table className="dense-table w-full" data-testid="bi-top-brokerages">
            <thead>
              <tr>
                <th>Brokerage</th>
                <th className="text-right">Signed</th>
                <th className="text-right">All deals</th>
                <th className="text-right">Commitment</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key ?? row.label}>
                  <td className="text-stone-300">{row.label}</td>
                  <td className="text-right font-mono text-stone-100">
                    {row.measures.committedCount + row.measures.fundedCount}
                  </td>
                  <td className="text-right">
                    <DrillCount count={row.measures.count} ids={row.measures.dealIds} title={`Deals from ${row.label}`} />
                  </td>
                  <td className="text-right font-mono text-stone-200">{millions(row.measures.commitment)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : null}
    </Panel>
  )
}

export function BIDashboardLive() {
  return (
    <div className="space-y-3 p-4">
      <section className="card px-5 py-4">
        <div className="flex items-center gap-2">
          <BarChart3 size={14} className="text-amber-400" />
          <span className="terminal-kicker">BI Dashboard</span>
        </div>
        <p className="mt-2 max-w-3xl text-[12px] leading-relaxed text-stone-400">
          Funded volume and stage conversion read the same per-deal figures as the{' '}
          <Link to="/pipeline" className="text-amber-300 hover:text-amber-200">Pipeline</Link>; every count opens
          the deals it counts. Panels that need a definition or a source the platform does not have say so.
        </p>
      </section>
      <div className="grid gap-3 lg:grid-cols-2">
        <MonthlyFunded />
        <StageConversion />
      </div>
      <TopSignedBrokerages />
      <div className="grid gap-3 lg:grid-cols-3">
        <TimeToFund />
        <Panel title="Originator scorecard · revenue attribution">
          <p className="text-[11px] leading-relaxed text-stone-500">
            Definitions pending. Member revenue is left blank until a revenue definition is agreed (register R10),
            and the originator and broker scorecard field sets are not yet defined (R11). Nothing is ranked until they are.
          </p>
        </Panel>
        <Panel title="Client acquisition funnel">
          <p className="text-[11px] leading-relaxed text-stone-500">
            Needs inquiry outcomes (qualified, quoted, won, lost) recorded per deal, and the dead deals from Backshop,
            neither of which is loaded yet.
          </p>
        </Panel>
      </div>
      <DrillDownDialog />
    </div>
  )
}
